import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Character, EntitySummary, Organization } from '@/lib/types';

import { RelationshipGraph, type RelationshipGraphEntity } from '@/components/relationship-graph';

type OrganizationMembersGraphProps = {
  organization: Organization;
  characters: EntitySummary[] | null;
  organizations: EntitySummary[] | null;
  novelId?: string | null;
  novelTheme?: string | null;
  isLoading?: boolean;
  onMemberClick?: (characterId: string) => void;
  onOrganizationClick?: (organizationId: string) => void;
};

const GRAPH_CANVAS_HEIGHT = 560;

function getFallbackEntitySummary(id: string, name: string, type: string): EntitySummary {
  return {
    id,
    name,
    type,
    shortDescription: '',
    primaryImageId: null,
    updatedAt: '',
  };
}

function getEntityUpdatedAt(entity: EntitySummary | undefined, fallback: EntitySummary) {
  return entity?.updatedAt || fallback.updatedAt;
}

// removed getOrganizationUpdatedAt

export function OrganizationMembersGraph({
  organization,
  characters,
  organizations,
  novelId,
  novelTheme,
  isLoading = false,
  onMemberClick,
  onOrganizationClick,
}: OrganizationMembersGraphProps) {
  const { t } = useTranslation();

  const memberGraphEntities = useMemo<RelationshipGraphEntity[]>(() => {
    const titleById = new Map((organization.organizationTitles || []).map((title) => [title.id, title.name]));
    const characterById = new Map((characters || []).map((character) => [character.id, character]));
    const organizationById = new Map((organizations || []).map((item) => [item.id, item]));
    const seenEntityKeys = new Set<string>();

    const characterEntities = (organization.organizationMembers || []).flatMap<RelationshipGraphEntity>((member) => {
      const entityKey = `characters:${member.characterId}`;
      if (seenEntityKeys.has(entityKey)) return [];
      seenEntityKeys.add(entityKey);

      const character = characterById.get(member.characterId);
      const fallback = getFallbackEntitySummary(member.characterId, member.characterId, 'Character');
      const title = member.title || (member.titleId ? titleById.get(member.titleId) : '');

      return [{
        id: character?.id || fallback.id,
        name: character?.name || fallback.name,
        type: character?.type || fallback.type,
        shortDescription: character?.shortDescription || fallback.shortDescription,
        primaryImageId: character?.primaryImageId ?? fallback.primaryImageId,
        mainImageUrl: character?.mainImageUrl,
        mainImagePath: character?.mainImagePath,
        graphPosition: organization.organizationMemberGraphPositions?.[`characters:${member.characterId}`],
        updatedAt: getEntityUpdatedAt(character, fallback),
        entityType: 'characters',
        graphTypeLabel: title || t('entity_form.organizations.membership.no_member_title'),
      }];
    });

    const organizationEntities = (organization.childOrganizationIds || []).flatMap<RelationshipGraphEntity>((organizationId) => {
      const entityKey = `organizations:${organizationId}`;
      if (seenEntityKeys.has(entityKey)) return [];
      seenEntityKeys.add(entityKey);

      const childOrganization = organizationById.get(organizationId);
      const fallback = getFallbackEntitySummary(organizationId, organizationId, 'Organization');

      return [{
        id: childOrganization?.id || fallback.id,
        name: childOrganization?.name || fallback.name,
        type: childOrganization?.type || fallback.type,
        shortDescription: childOrganization?.shortDescription || fallback.shortDescription,
        primaryImageId: childOrganization?.primaryImageId ?? fallback.primaryImageId,
        mainImageUrl: childOrganization?.mainImageUrl,
        mainImagePath: childOrganization?.mainImagePath,
        graphPosition: organization.organizationMemberGraphPositions?.[`organizations:${organizationId}`],
        updatedAt: getEntityUpdatedAt(childOrganization, fallback),
        entityType: 'organizations',
        graphTypeLabel: organization.childOrganizationTitles?.[organizationId]
          || childOrganization?.type
          || t('entity_form.organizations.membership_graph.sub_organization'),
      }];
    });

    return [...characterEntities, ...organizationEntities];
  }, [
    characters,
    organization.childOrganizationIds,
    organization.childOrganizationTitles,
    organization.organizationMemberGraphPositions,
    organization.organizationMembers,
    organization.organizationTitles,
    organizations,
    t,
  ]);

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2, display: 'grid', placeItems: 'center', minHeight: 300 }}>
        <CircularProgress size={28} />
      </Paper>
    );
  }

  if (memberGraphEntities.length === 0) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h2" sx={{ mb: 1.5 }}>
          {t('entity_form.organizations.membership_graph.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('entity_form.organizations.membership_graph.empty')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h2" sx={{ mb: 1 }}>
        {t('entity_form.organizations.membership_graph.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('entity_form.organizations.membership_graph.description')}
      </Typography>
      <Box sx={{ '& .react-flow__node': { fontFamily: 'inherit' } }}>
        <RelationshipGraph
          entities={memberGraphEntities}
          relationships={organization.organizationMemberGraphRelationships || []}
          codexCategories={{}}
          novelId={novelId}
          novelTheme={novelTheme}
          shapeThemeOverride={organization.organizationMemberGraphTheme}
          onEntityClick={(entityType, entityId) => {
            if (entityType === 'characters') {
              onMemberClick?.(entityId);
              return;
            }

            onOrganizationClick?.(entityId);
          }}
          enableLayoutEditing={false}
          showShapeThemeSelector={false}
          showEntityTooltip={false}
          fitViewDependency={organization}
          height={GRAPH_CANVAS_HEIGHT}
          minHeight={GRAPH_CANVAS_HEIGHT}
        />
      </Box>
    </Paper>
  );
}
